import { addLocale, useLocale } from "ttag";
import type { TTagTranslation, TTagTranslations } from "ttag/dist/config";

import { ThelabuiContextProcessorsTranslationProcessor as TranslationProcessor } from "@reactivated";
import type { ThelabuiContextProcessorsTranslationProcessor } from "@reactivated";

import { objectKeys } from "./functional";
import { memoizeOne } from "./memoize";

/**
 * Accept a set of translations from Reactivated context, load them into ttag,
 * and activate the locale.
 */
export const activateTranslations = (
    i18n: ThelabuiContextProcessorsTranslationProcessor["i18n"],
) => {
    const translations: TTagTranslations = {
        charset: "utf-8",
        headers: {
            "plural-forms": "nplurals=2; plural=(n!=1);",
        },
        translations: {
            "": Object.fromEntries(
                i18n.catalog.map((entry): [string, TTagTranslation] => [
                    entry.key,
                    {
                        msgid: entry.key,
                        msgstr: [entry.value],
                    },
                ]),
            ),
        },
    };
    addLocale(i18n.language.code, translations);
    /* eslint-disable-next-line react-hooks/rules-of-hooks */
    useLocale(i18n.language.code);
};

/**
 * @deprecated since version 0.11.0.
 */
const getCatalogue = memoizeOne((context: TranslationProcessor) => {
    return new Map(context.i18n.catalog.map(({ key, value }) => [key, value]));
});

/**
 * Get a translated string. For more information, see Django's documentation on
 * [Translation](https://docs.djangoproject.com/en/4.2/topics/i18n/translation).
 *
 * @deprecated since version 0.11.0. Use ttag instead.
 */
export const gettext = (
    text: string,
    context: TranslationProcessor,
): string => {
    const catalog = getCatalogue(context);
    return catalog.get(text) || text;
};

type Specifiers = {
    s: string;
    d: number;
    b: boolean;
};

type Spec = keyof Specifiers;

/* eslint-disable unused-imports/no-unused-vars */
type ValuesPositional<T extends string> =
    T extends `${infer _}%${infer K}${infer Rest}`
        ? K extends Spec
            ? [Specifiers[K], ...ValuesPositional<Rest>]
            : ValuesPositional<`${K}${Rest}`>
        : [];

type ValuesNamed<T extends string> =
    T extends `${infer _}%(${infer N})${infer K}${infer Rest}`
        ? K extends Spec
            ? ValuesNamed<Rest> & {
                  [Key in N]: Specifiers[K];
              }
            : ValuesNamed<`(${N})${K}${Rest}`>
        : object;
/* eslint-enable unused-imports/no-unused-vars */

type FormattedPositional<T extends string> =
    T extends `${infer Head}%${infer K}${infer Tail}`
        ? K extends Spec
            ? `${Head}${string}${FormattedPositional<Tail>}`
            : `${Head}%${FormattedPositional<`${K}${Tail}`>}`
        : T;

type FormattedNamed<T extends string> =
    T extends `${infer Head}%(${infer N})${infer K}${infer Tail}`
        ? K extends Spec
            ? `${Head}${string}${FormattedNamed<Tail>}`
            : `${Head}%${FormattedNamed<`(${N})${K}${Tail}`>}`
        : T;

const placeholders: { [T in Spec]: null } = {
    s: null,
    d: null,
    b: null,
};

const allPlaceholders = objectKeys(placeholders).join("");
const placeholderPatternPositional = new RegExp(`%([${allPlaceholders}])`, "g"); // nosemgrep: eslint.detect-non-literal-regexp
const placeholderPatternNamed = new RegExp(
    `%\\(([^)]+)\\)([${allPlaceholders}])`,
    "g",
); // nosemgrep: eslint.detect-non-literal-regexp

/**
 * Type-safe implementation of interpolate / sprintf with either named or positional arguments.
 *
 * @deprecated since version 0.11.0. Use ttag instead.
 *
 * See:
 *
 * - https://www.hacklewayne.com/a-truly-strongly-typed-printf-in-typescript
 * - https://github.com/hackle/blogger/blob/master/src/sample/printf.ts#L17
 */
export function interpolate<T extends string>(
    fmt: T,
    values: ValuesPositional<T>,
): FormattedPositional<T>;

/**
 * Type-safe implementation of interpolate / sprintf with either named or positional arguments.
 *
 * @deprecated since version 0.11.0. Use ttag instead.
 *
 * See:
 *
 * - https://www.hacklewayne.com/a-truly-strongly-typed-printf-in-typescript
 * - https://github.com/hackle/blogger/blob/master/src/sample/printf.ts#L17
 */
export function interpolate<T extends string>(
    fmt: T,
    values: ValuesNamed<T>,
): FormattedNamed<T>;

/**
 * Type-safe implementation of interpolate / sprintf with either named or positional arguments.
 *
 * @deprecated since version 0.11.0. Use ttag instead.
 *
 * See:
 *
 * - https://www.hacklewayne.com/a-truly-strongly-typed-printf-in-typescript
 * - https://github.com/hackle/blogger/blob/master/src/sample/printf.ts#L17
 */
export function interpolate<T extends string>(
    fmt: T,
    values: ValuesPositional<T> | ValuesNamed<T>,
): FormattedPositional<T> | FormattedNamed<T> {
    // Handle positional interpolation
    if (values instanceof Array) {
        let i = 0;
        const formatted = fmt.replace(placeholderPatternPositional, () => {
            const value = values[i++] as Specifiers[Spec];
            return value.toString();
        });
        return formatted as FormattedPositional<T>;
    }
    // Handle named interpolation
    const formatted = fmt.replace(
        placeholderPatternNamed,
        (_match, name: keyof ValuesNamed<T>) => {
            const value = values[name] as Specifiers[Spec];
            return value.toString();
        },
    );
    return formatted as FormattedNamed<T>;
}
